package com.evoapps.evoshift.ui.util

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.AcUnit
import androidx.compose.material.icons.filled.Cloud
import androidx.compose.material.icons.filled.DeviceThermostat
import androidx.compose.material.icons.filled.Thunderstorm
import androidx.compose.material.icons.filled.Umbrella
import androidx.compose.material.icons.filled.WbSunny
import androidx.compose.material.icons.filled.WaterDrop
import androidx.compose.material3.Icon
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.DayWeather
import com.evoapps.evoshift.domain.model.WeatherCondition
import com.evoapps.evoshift.domain.model.WeatherSource
import com.evoapps.evoshift.domain.model.WeatherSummary
import com.evoapps.evoshift.ui.finance.formatDecimal
import com.evoapps.evoshift.ui.adaptive.ScrollingRow
import com.evoapps.evoshift.widgets.RegularCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.weather_attribution_imgw
import evoshift.composeapp.generated.resources.weather_attribution_met
import evoshift.composeapp.generated.resources.weather_cloudy
import evoshift.composeapp.generated.resources.weather_degrees
import evoshift.composeapp.generated.resources.weather_forecast_description
import evoshift.composeapp.generated.resources.weather_forecast_marker
import evoshift.composeapp.generated.resources.weather_observed_description
import evoshift.composeapp.generated.resources.weather_partly_cloudy
import evoshift.composeapp.generated.resources.weather_precipitation
import evoshift.composeapp.generated.resources.weather_precipitation_chance
import evoshift.composeapp.generated.resources.weather_rain
import evoshift.composeapp.generated.resources.weather_range
import evoshift.composeapp.generated.resources.weather_snow
import evoshift.composeapp.generated.resources.weather_storm
import evoshift.composeapp.generated.resources.weather_strip_empty
import evoshift.composeapp.generated.resources.weather_strip_title
import evoshift.composeapp.generated.resources.weather_sunny
import evoshift.composeapp.generated.resources.weather_unknown
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

// SH-226: the workplace's weather (functions/weather.js writes it; docs/weather.md). The icons and
// texts take the colour of what they sit on (LocalContentColor), so they follow the appearance and
// the card or table they are in; a condition is never told by colour alone: each has its own
// shape and its name is read out (and shown in the strip).

/** Each day's weather on the daily results, by ISO date (empty: weather off or none yet). */
val LocalDayWeather = staticCompositionLocalOf<Map<String, DayWeather>> { emptyMap() }

/** The daily results' and roster's weather badge, by ISO date. */
fun weatherBadgeTag(isoDate: String) = "weather_badge_$isoDate"
/** The dashboard's "Weather in the coming days" card. */
const val WEATHER_STRIP_TAG = "weather_strip"
/** The attribution line under weather. */
const val WEATHER_ATTRIBUTION_TAG = "weather_attribution"

fun WeatherCondition?.labelRes(): StringResource = when (this) {
    WeatherCondition.SUNNY -> Res.string.weather_sunny
    WeatherCondition.PARTLY_CLOUDY -> Res.string.weather_partly_cloudy
    WeatherCondition.CLOUDY -> Res.string.weather_cloudy
    WeatherCondition.RAIN -> Res.string.weather_rain
    WeatherCondition.SNOW -> Res.string.weather_snow
    WeatherCondition.STORM -> Res.string.weather_storm
    null -> Res.string.weather_unknown
}

/** The icon of a condition; partly cloudy is drawn as a sun with a cloud ([WeatherIcon]). */
fun WeatherCondition?.icon(): ImageVector = when (this) {
    WeatherCondition.SUNNY -> Icons.Default.WbSunny
    WeatherCondition.PARTLY_CLOUDY -> Icons.Default.Cloud
    WeatherCondition.CLOUDY -> Icons.Default.Cloud
    WeatherCondition.RAIN -> Icons.Default.Umbrella
    WeatherCondition.SNOW -> Icons.Default.AcUnit
    WeatherCondition.STORM -> Icons.Default.Thunderstorm
    null -> Icons.Default.DeviceThermostat
}

/** A condition's icon in the surrounding content colour; [described]: its name is read out. */
@Composable
fun WeatherIcon(condition: WeatherCondition?, size: Dp = 18.dp, described: Boolean = false, modifier: Modifier = Modifier) {
    val label = if (described) stringResource(condition.labelRes()) else null
    val tint = LocalContentColor.current
    if (condition == WeatherCondition.PARTLY_CLOUDY) {
        // A sun half behind a cloud: the shape tells it from sunny and cloudy.
        Box(modifier.size(size).let { m -> if (label != null) m.clearAndSetSemantics { contentDescription = label } else m }) {
            Icon(Icons.Default.WbSunny, null, tint = tint, modifier = Modifier.size(size * 0.7f).align(Alignment.TopStart))
            Icon(Icons.Default.Cloud, null, tint = tint, modifier = Modifier.size(size * 0.72f).align(Alignment.BottomEnd).offset(x = 1.dp))
        }
    } else {
        Icon(condition.icon(), contentDescription = label, tint = tint, modifier = modifier.size(size))
    }
}

/** "Rain, 9 to 18 °C" (measured) or "Forecast: rain, 9 to 18 °C", for screen readers. */
@Composable
fun weatherDescription(day: DayWeather): String? {
    val shown = day.shown ?: return null
    val condition = stringResource(shown.condition.labelRes()).lowercase()
    return stringResource(
        if (day.isObserved) Res.string.weather_observed_description else Res.string.weather_forecast_description,
        condition, shown.minRounded, shown.maxRounded
    )
}

/**
 * A day's weather in a line: the condition's icon and the high ("☂ 18°"), or the range when
 * [range]. A forecast for a day already past is marked "forecast" ([markForecast]). One node for
 * screen readers with the whole description.
 */
@Composable
fun WeatherBadge(
    day: DayWeather,
    modifier: Modifier = Modifier,
    range: Boolean = false,
    markForecast: Boolean = false,
    iconSize: Dp = 16.dp,
    textStyle: TextStyle = MaterialTheme.typography.labelMedium
) {
    val shown = day.shown ?: return
    val description = weatherDescription(day).orEmpty()
    Row(
        modifier = modifier
            .testTag(weatherBadgeTag(day.date.toString()))
            // One node read out as the whole sentence; the texts stay in the tree (contrast checks).
            .semantics(mergeDescendants = true) { contentDescription = description },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        WeatherIcon(shown.condition, size = iconSize)
        Text(
            temperatureText(shown, range),
            style = textStyle,
            maxLines = 1
        )
        if (markForecast && !day.isObserved) {
            Text(
                stringResource(Res.string.weather_forecast_marker),
                style = MaterialTheme.typography.labelSmall,
                maxLines = 1
            )
        }
    }
}

@Composable
private fun temperatureText(shown: WeatherSummary, range: Boolean): String =
    if (range) stringResource(Res.string.weather_range, shown.minRounded, shown.maxRounded)
    else stringResource(Res.string.weather_degrees, shown.maxRounded)

/**
 * The credit the sources' licences ask for wherever their data is shown (docs/weather.md): MET
 * Norway's forecast (CC BY 4.0, processed) and IMGW-PIB's observations (their wording, which also
 * says the data was processed).
 */
@Composable
fun WeatherAttribution(sources: Set<WeatherSource>, modifier: Modifier = Modifier) {
    if (sources.isEmpty()) return
    Column(modifier.testTag(WEATHER_ATTRIBUTION_TAG)) {
        if (WeatherSource.MET_NORWAY in sources) {
            Text(stringResource(Res.string.weather_attribution_met), style = MaterialTheme.typography.labelSmall)
        }
        if (WeatherSource.IMGW in sources) {
            Text(stringResource(Res.string.weather_attribution_imgw), style = MaterialTheme.typography.labelSmall)
        }
    }
}

/**
 * SH-226: "Weather in the coming days" on the owner's and managers' dashboards: each day's weekday,
 * the condition (icon and name), the high and low and the chance of rain, then the credit.
 */
@Composable
fun WeatherForecastCard(days: List<DayWeather>, today: LocalDate?, modifier: Modifier = Modifier) {
    RegularCard(modifier = modifier.fillMaxWidth().testTag(WEATHER_STRIP_TAG)) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Text(
                stringResource(Res.string.weather_strip_title),
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold)
            )
            if (days.isEmpty()) {
                Text(stringResource(Res.string.weather_strip_empty), style = MaterialTheme.typography.bodySmall)
            } else {
                ScrollingRow(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                    days.forEach { day -> ForecastDay(day, isToday = day.date == today) }
                }
                WeatherAttribution(DayWeather.sourcesOf(days))
            }
        }
    }
}

@Composable
private fun ForecastDay(day: DayWeather, isToday: Boolean) {
    val shown = day.forecast ?: day.shown ?: return
    val description = weatherDescription(day.copy(observed = null)).orEmpty()
    val chance = shown.precipitationProbability?.takeIf { it >= 10 }
    val chanceText = chance?.let { stringResource(Res.string.weather_precipitation_chance, it) }
    val rainText = shown.precipitationMm?.takeIf { it >= 0.1 }?.let { stringResource(Res.string.weather_precipitation, formatDecimal(it, 1)) }
    val weekday = dayShortName(day.date)
    Column(
        modifier = Modifier
            .width(64.dp)
            .testTag(weatherBadgeTag(day.date.toString()))
            .semantics(mergeDescendants = true) {
                contentDescription = listOfNotNull("$weekday ${day.date.day}", description, chanceText, rainText).joinToString(", ")
            },
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(2.dp)
    ) {
        Text(
            "$weekday ${day.date.day}",
            style = MaterialTheme.typography.labelMedium,
            fontWeight = if (isToday) FontWeight.Bold else FontWeight.Normal,
            maxLines = 1
        )
        WeatherIcon(shown.condition, size = 24.dp)
        Text(
            stringResource(shown.condition.labelRes()),
            style = MaterialTheme.typography.labelSmall,
            textAlign = TextAlign.Center,
            maxLines = 2
        )
        Text(stringResource(Res.string.weather_range, shown.minRounded, shown.maxRounded), style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold)
        if (chance != null) {
            // The chance of rain: a drop and the percentage (the whole sentence is read out above).
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(2.dp)) {
                Icon(Icons.Default.WaterDrop, null, tint = LocalContentColor.current, modifier = Modifier.size(12.dp))
                Text("$chance%", style = MaterialTheme.typography.labelSmall)
            }
        }
    }
}

@Composable
private fun dayShortName(date: LocalDate): String = dayShort(date.dayOfWeek)

/**
 * The hook for SH-225's comparisons ("−18%, rain"): the day's condition in a word, lower case, or
 * null without weather. A comparison of two days reads `LocalDayWeather` (or its view model's
 * weather map) for each date and appends this to the change.
 */
@Composable
fun weatherComparisonNote(day: DayWeather?): String? {
    val shown = day?.shown ?: return null
    return stringResource(shown.condition.labelRes()).lowercase()
}
